import {
  computeCacheKey,
  fetchTranslationFromApi,
  initializePromiser,
  readFromDatabase,
  writeToDatabase,
} from './localTranslationCacheRepository'
import LruCache from './LruCache'

class ChatTranslationCache {
  constructor() {
    this.disabled = false
    this.promiserPromise = null
    this.memoryCache = new LruCache()
    this.pending = new Map()
  }

  async translate({ targetLanguage, text, context }) {
    if (!targetLanguage || !text) {
      throw new Error('targetLanguage and text are required')
    }

    const cacheKey = await computeCacheKey(targetLanguage, text, context)

    if (this.memoryCache.has(cacheKey)) {
      return this.memoryCache.get(cacheKey)
    }

    if (this.pending.has(cacheKey)) {
      return this.pending.get(cacheKey)
    }

    const operation = this.translateInternal(cacheKey, { targetLanguage, text, context }).finally(() => {
      this.pending.delete(cacheKey)
    })

    this.pending.set(cacheKey, operation)

    return operation
  }

  async translateInternal(cacheKey, payload) {
    const promiser = await this.getPromiser()

    if (promiser) {
      const cached = await readFromDatabase(promiser, cacheKey)

      if (cached) {
        this.memoryCache.set(cacheKey, cached)
        return cached
      }
    }

    const translation = await fetchTranslationFromApi(payload)

    this.memoryCache.set(cacheKey, translation)

    if (promiser) {
      await writeToDatabase(promiser, cacheKey, payload, translation)
    }

    return translation
  }

  async getPromiser() {
    if (this.disabled) {
      return null
    }

    if (typeof window === 'undefined' || !window.Worker) {
      this.disabled = true
      return null
    }

    if (!this.promiserPromise) {
      this.promiserPromise = initializePromiser().catch((error) => {
        // eslint-disable-next-line no-console
        console.warn('[ChatTranslationCache] Failed to initialize sqlite-wasm worker.', error)
        this.disabled = true

        return null
      })
    }

    return this.promiserPromise
  }
}

const chatTranslationCache = new ChatTranslationCache()

export default chatTranslationCache
